import logger from './logger';

// Storage thresholds
const CRITICAL_THRESHOLD = 0.95; // 95% - stop recording
const WARNING_THRESHOLD = 0.85; // 85% - show warning
const MINIMUM_FREE_SPACE_MB = 100; // Minimum 100MB free space required

export interface StorageQuota {
  usage: number;
  quota: number;
  percentUsed: number;
  availableSpace: number;
  isCritical: boolean;
  isWarning: boolean;
  hasSufficientSpace: boolean;
}

/**
 * Check current storage quota status
 * Returns detailed storage information for decision making
 */
export const checkStorageQuota = async (): Promise<StorageQuota> => {
  try {
    if (!navigator.storage || !navigator.storage.estimate) {
      logger.warn('Storage API not available, assuming sufficient space');
      return {
        usage: 0,
        quota: Number.MAX_SAFE_INTEGER,
        percentUsed: 0,
        availableSpace: Number.MAX_SAFE_INTEGER,
        isCritical: false,
        isWarning: false,
        hasSufficientSpace: true,
      };
    }

    const estimate = await navigator.storage.estimate();
    const usage = estimate.usage || 0;
    const quota = estimate.quota || Number.MAX_SAFE_INTEGER;
    const availableSpace = quota - usage;
    const percentUsed = quota > 0 ? usage / quota : 0;

    const isCritical = percentUsed >= CRITICAL_THRESHOLD;
    const isWarning = percentUsed >= WARNING_THRESHOLD;
    const hasSufficientSpace =
      availableSpace >= MINIMUM_FREE_SPACE_MB * 1024 * 1024;

    logger.debug('Storage quota check:', {
      usage: `${(usage / 1024 / 1024).toFixed(2)} MB`,
      quota: `${(quota / 1024 / 1024).toFixed(2)} MB`,
      percentUsed: `${(percentUsed * 100).toFixed(2)}%`,
      availableSpace: `${(availableSpace / 1024 / 1024).toFixed(2)} MB`,
      isCritical,
      isWarning,
      hasSufficientSpace,
    });

    return {
      usage,
      quota,
      percentUsed,
      availableSpace,
      isCritical,
      isWarning,
      hasSufficientSpace,
    };
  } catch (error) {
    logger.error('Failed to check storage quota:', error);
    // On error, assume we have space to avoid blocking legitimate operations
    return {
      usage: 0,
      quota: Number.MAX_SAFE_INTEGER,
      percentUsed: 0,
      availableSpace: Number.MAX_SAFE_INTEGER,
      isCritical: false,
      isWarning: false,
      hasSufficientSpace: true,
    };
  }
};

/**
 * Check if there's enough space for a given operation
 * @param requiredBytes - Number of bytes needed for the operation
 */
export const hasEnoughSpace = async (
  requiredBytes: number,
): Promise<boolean> => {
  const quota = await checkStorageQuota();

  // If critical threshold reached, deny
  if (quota.isCritical) {
    logger.warn('Storage at critical level, denying operation');
    return false;
  }

  // Check if available space exceeds required bytes
  if (quota.availableSpace < requiredBytes) {
    logger.warn(
      `Insufficient space: need ${requiredBytes} bytes, have ${quota.availableSpace} bytes`,
    );
    return false;
  }

  return true;
};

/**
 * Circuit breaker for storage-intensive operations
 * Prevents operations when storage is critically low
 */
export class StorageCircuitBreaker {
  private isOpen = false;

  private lastCheck = 0;

  private checkInterval = 5000; // Check every 5 seconds max

  async shouldAllowOperation(operationSizeBytes = 0): Promise<boolean> {
    const now = Date.now();

    // Rate limit quota checks
    if (now - this.lastCheck < this.checkInterval && !this.isOpen) {
      // If circuit was closed recently, allow operation
      return !this.isOpen;
    }

    this.lastCheck = now;

    const quota = await checkStorageQuota();

    // Open circuit if critical
    if (quota.isCritical || !quota.hasSufficientSpace) {
      if (!this.isOpen) {
        logger.error(
          'Storage circuit breaker OPENED - critical storage level reached',
        );
        this.isOpen = true;
      }
      return false;
    }

    // Check if specific operation fits
    if (operationSizeBytes > 0 && quota.availableSpace < operationSizeBytes) {
      logger.warn(
        `Operation size ${operationSizeBytes} exceeds available space ${quota.availableSpace}`,
      );
      return false;
    }

    // Close circuit if we were open but now have space
    if (this.isOpen && !quota.isCritical && quota.hasSufficientSpace) {
      logger.info(
        'Storage circuit breaker CLOSED - sufficient storage available',
      );
      this.isOpen = false;
    }

    return !this.isOpen;
  }

  isCircuitOpen(): boolean {
    return this.isOpen;
  }

  reset(): void {
    this.isOpen = false;
    this.lastCheck = 0;
  }
}

// Global circuit breaker instance
export const globalStorageCircuitBreaker = new StorageCircuitBreaker();
